import { useEffect, useMemo, useRef } from 'react'
import { calculateChange, toLocaleStringIn } from '../util/general'
import { useSelector } from 'react-redux'
import { selectSnapshot, selectSnapshots, selectUserSecurityExpressions } from '../selectors/security'
import { selectRecentExpressions, selectRecentExpressionsRefreshTime } from '../selectors/common'
import { RecentExpression } from '../reducer/common.reducer'
import { selectPrimarySecurity } from '../selectors/home'
import { getExpressionSecurityKey, getSecurityKey } from '../util/security'
import { convertExpressionToEvaluationExpression, getExpressionFromSaved } from '../util/expression'
import math from '../util/math'
import { WebSocketRequestMessage_Subscription } from '../proto/generated/websocket'
import websocketActions from '../actions/websocket.actions'
import { useDispatch } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'
import ROUTES from '../assets/routes'

const PageWrapper = ({ children }: any) => {
    const { key, expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    const recentExpressionsRefreshedTime = useSelector(selectRecentExpressionsRefreshTime)
    const dispatch = useDispatch()
    const location = useLocation()
    const recentExpressions = useSelector(selectRecentExpressions)
    const snapshots = useSelector(selectSnapshots)
    const mainSecurity = expressionMode ? null : (underlying || primary)
    const savedExpressions = useSelector(selectUserSecurityExpressions)
    const securityToken = useMemo(()=>getSecurityKey(mainSecurity?.exchange??0, mainSecurity?.code??''), [mainSecurity])
    const mainSecuritySnapshot = useSelector(selectSnapshot(securityToken))
    const prevToken = useRef<string>('')

    useEffect(()=>{
        prevToken.current = securityToken
    }, [])

    const displaySymbol = useMemo(() => {
        if (!expressionMode) {
            return mainSecurity?.symbol || 'Security'
        }
        const savedExpression = getExpressionFromSaved(savedExpressions, key)
        if (savedExpression && savedExpression.name) {
            return savedExpression.name
        }
        return 'Spread'
    }, [expressionMode, savedExpressions, key, mainSecurity?.symbol])

    useEffect(() => {
        if (expressionMode) {
            try {
                const {
                    expression: evaluationExpression,
                    context
                } = convertExpressionToEvaluationExpression(key, expressionSecurities)
                const ltp: Record<string, any> = { ...context }
                const change: Record<string, any> = { ...context }
                const previousClose: Record<string, any> = { ...context }

                for (let c = 0; c < expressionSecurities.length; c++) {
                    const sec = expressionSecurities[c]
                    const token = getSecurityKey(sec.exchange, sec.code)
                    const sn = snapshots[token]
                    if (!sn || !sec) return
                    const expressionKey = getExpressionSecurityKey(sec.exchange, sec.code)
                    const priceMultiplier = sec.priceMultiplier || 100
                    change[expressionKey] = sn.lastTradedPrice / priceMultiplier - sn.previousClose / priceMultiplier
                    ltp[expressionKey] = sn.lastTradedPrice / priceMultiplier
                    previousClose[expressionKey] = sn.previousClose / priceMultiplier
                }
                const ltpResult: number = math.evaluate(evaluationExpression, ltp)
                const changeResult: number = math.evaluate(evaluationExpression, change)
                const previousCloseResult: number = math.evaluate(evaluationExpression, previousClose)

                const changePercentage = changeResult * 100 / previousCloseResult
                const positive = changePercentage > 0
                const negative = changePercentage < 0
                let newTitle = `${displaySymbol} ${toLocaleStringIn(ltpResult)}`
                if (ltpResult >= 0) {
                    newTitle += ` ${positive ? '▲' : negative ? '▼' : ''} ${positive ? '+' : ''}${positive || negative ? changePercentage.toFixed(2) + '%' : ''}`
                }
                if (newTitle !== document.title)
                    document.title = newTitle
            } catch (e) {
                document.title = 'tick2trade - Track All Markets'
            }
        } else {
            if (!mainSecuritySnapshot || !mainSecurity) {
                document.title = 'tick2trade - Track All Markets'
                return
            }
            const change = calculateChange(mainSecuritySnapshot.lastTradedPrice, mainSecuritySnapshot.previousClose)
            const positive = change.changePercentage > 0
            const negative = change.changePercentage < 0
            const newTitle = `${displaySymbol} ${toLocaleStringIn(mainSecuritySnapshot.lastTradedPrice / (mainSecurity.priceMultiplier ?? 100))} ${positive ? '▲' : negative ? '▼' : ''} ${positive ? '+' : ''}${positive || negative ? change.changePercentage.toFixed(2) + '%' : ''}`
            if (newTitle !== document.title)
                document.title = newTitle
        }
    }, [mainSecuritySnapshot, mainSecurity, expressionMode, key, expressionSecurities, snapshots, displaySymbol])

    useEffect(() => {
        if (Math.abs(new Date().getTime() - recentExpressionsRefreshedTime) > (24 * 60 * 60 * 1000)) {
            const expressionToRecentTime: Record<string, number> = {}
            const securityExpressions = recentExpressions.map((expression: RecentExpression) => {
                expressionToRecentTime[expression.expression] = expression.lastUsedTime
                return {
                    id: expression.expression,
                    name: '',
                    expression: expression.expression,
                    securities: expression.securities,
                    expired: false
                }
            })
        }
    }, [recentExpressionsRefreshedTime])

    useEffect(() => {
        const requiredUrl = [ROUTES.optionChain, ROUTES.strategyBuilder, ROUTES.openInterest, ROUTES.openInterestChange, ROUTES.multiStrikeChart, ROUTES.symbol, ROUTES.spread].some(route => matchPath({ path: route }, location.pathname))
        if(mainSecurity && requiredUrl && prevToken.current!==securityToken) {
            prevToken.current = securityToken
            const payload: Partial<WebSocketRequestMessage_Subscription> = {
                securitySnapshotSubscriptions: [{
                    tag: 'primary-security',
                    securities: [{
                            exchange: mainSecurity.exchange,
                            code: mainSecurity.code
                        }]
                }]
            }
            websocketActions.setWebsocketSubscribeList(dispatch, payload)
        }
    }, [mainSecurity, location.pathname])

    return children

}

export default PageWrapper